// src/pages/SupportPage.jsx
// Support Ticket Management — follows same UI pattern as TaskCenterPage & CreditsPage
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Ticket, Plus, Search, RefreshCw, ChevronRight, MessageSquare,
  AlertTriangle, Clock, CheckCircle2, XCircle, ArrowUpCircle,
  LifeBuoy, Tag, Lock, Send, Paperclip, Filter, MoreVertical,
  Activity, Inbox, ChevronDown, User, Shield, Zap, X,
  TriangleAlert, ArrowRight
} from 'lucide-react';
import { apiFetch, apiFetchBlob } from '../api';
import { isPlatformScoped } from '../utils/permissions.js';
import { LoadingSpinner, PageHeader } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { useAuth } from '../context/useAuth';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import dayjs from 'dayjs';
import { useTenantExportBranding } from '../hooks/useTenantExportBranding.js';
import packageJson from '../../package.json';
// ─── Helpers ──────────────────────────────────────────────────────────────────
// Helpers removed — replaced by useTimezoneFormatter hook

// ─── Badge Configs ─────────────────────────────────────────────────────────────
const STATUS_META = {
  Open:               { label: 'Open',        color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',       dot: 'bg-blue-500' },
  InProgress:         { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300', dot: 'bg-yellow-500' },
  WaitingForCustomer: { label: 'Waiting',     color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', dot: 'bg-purple-500' },
  Resolved:           { label: 'Resolved',    color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',   dot: 'bg-green-500' },
  Closed:             { label: 'Closed',      color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',          dot: 'bg-gray-400' },
  Escalated:          { label: 'Escalated',   color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',           dot: 'bg-red-500' },
};
const PRIORITY_META = {
  Low:      { color: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',          icon: '▼' },
  Medium:   { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300', icon: '●' },
  High:     { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', icon: '▲' },
  Critical: { color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',             icon: '🔴' },
};
const VALID_NEXT = {
  Open:               ['InProgress', 'Escalated'],
  InProgress:         ['WaitingForCustomer', 'Resolved', 'Escalated'],
  WaitingForCustomer: ['InProgress', 'Escalated'],
  Resolved:           ['Closed'],
  Closed:             [],
  Escalated:          ['InProgress', 'Resolved'],
};

// ─── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.Open;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${m.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

// ─── Priority Badge ────────────────────────────────────────────────────────────
function PriorityBadge({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.Medium;
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${m.color}`}>
      {m.icon} {priority}
    </span>
  );
}

// ─── SLA Badge ────────────────────────────────────────────────────────────────
function SLABadge({ ticket, formatRelativeTime }) {
  if (!ticket.sla_response_due_at) return null;
  const now = dayjs().valueOf();
  const hasResp = !!ticket.first_response_at;
  const respDue = dayjs(ticket.sla_response_due_at).valueOf();
  const resDue  = ticket.sla_resolve_due_at ? dayjs(ticket.sla_resolve_due_at).valueOf() : null;
  const done    = ['Closed', 'Resolved'].includes(ticket.status);
  if (done) return <span className="text-xs font-semibold text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> SLA Met</span>;
  if (resDue && now > resDue) return <span className="text-xs font-bold text-red-600 dark:text-red-400 flex items-center gap-1"><TriangleAlert className="w-3 h-3" /> SLA Breached</span>;
  if (!hasResp && now > respDue) return <span className="text-xs font-bold text-red-600 dark:text-red-400 flex items-center gap-1"><TriangleAlert className="w-3 h-3" /> No Response</span>;
  if (!hasResp && (respDue - now) < 3600000) return <span className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {Math.round((respDue - now) / 60000)}m left</span>;
  return <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> SLA OK</span>;
}

// ─── S3 Attachment Upload ─────────────────────────────────────────────────────
async function uploadAttachmentToS3(token, ticketId, file) {
  const formData = new FormData();
  formData.append('file', file);
  // Use apiFetch — it uses NORMALIZED_API_BASE correctly and handles FormData
  return apiFetch(`/support/tickets/${ticketId}/upload`, token, {
    method: 'POST',
    body: formData,
  });
}

// ─── Create Ticket Modal ───────────────────────────────────────────────────────
function CreateTicketModal({ token, onClose, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ email: user?.email || '', subject: '', description: '', priority: 'Medium', category: '' });
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const payload = { ...form, build_version: packageJson.version };
      if (!payload.category) delete payload.category;
      // First create the ticket to get ticket_id
      const result = await apiFetch('/support/tickets', token, { method: 'POST', body: JSON.stringify(payload) });
      // Upload attachment — passes update_ticket=true so the s3_key is saved in support_tickets.attachment_path
      if (file && result.ticket_id) {
        setUploadProgress('Uploading attachment…');
        try {
          const fd = new FormData();
          fd.append('file', file);
          // update_ticket=true → backend writes s3_key into support_tickets.attachment_path
          await apiFetch(`/support/tickets/${result.ticket_id}/upload?update_ticket=true`, token, {
            method: 'POST',
            body: fd,
          });
        } catch (uploadErr) {
          console.warn('Attachment upload failed:', uploadErr.message);
        }
        setUploadProgress('');
      }
      onCreated(result);
    } catch (err) { setError(err.message || 'Failed to create ticket'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
              <Ticket className="w-4 h-4 text-primary dark:text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">New Support Ticket</h3>
              <p className="text-xs text-gray-400 dark:text-gray-500">Describe your issue and we'll get back to you</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs rounded-lg px-3 py-2.5">⚠️ {error}</div>}

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">Email <span className="text-red-400">*</span></label>
            <input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="your@email.com"
              className="w-full px-4 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">Subject <span className="text-red-400">*</span></label>
            <input required value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
              placeholder="Brief description of the issue"
              className="w-full px-4 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">Description <span className="text-red-400">*</span></label>
            <textarea required value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Detailed description of your issue..."
              rows={4}
              className="w-full px-4 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">Priority</label>
              <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30">
                {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">Category</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">— Select —</option>
                {['Billing', 'Technical', 'Access', 'Other'].map(c => <option key={c} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Attachment <span className="text-gray-300">(optional)</span></label>
            <div className="flex items-center gap-2">
              <input type="file" onChange={e => setFile(e.target.files?.[0] || null)}
                className="flex-1 text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/15 border border-gray-200 rounded-xl px-3 py-2" />
              {file && <span className="text-xs text-gray-400 truncate max-w-[120px]">{file.name}</span>}
            </div>
            {uploadProgress && <p className="text-xs text-primary mt-1">{uploadProgress}</p>}
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-xl transition-colors flex items-center justify-center gap-2">
              {loading ? <><DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /> Submitting…</> : <><Plus className="w-4 h-4" /> Submit Ticket</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Chat / Messages Panel ────────────────────────────────────────────────────
function ChatPanel({ ticket, isAdmin, token, currentUserId, user }) {
  const { formatRelativeTime } = useTimezoneFormatter();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showCanned, setShowCanned] = useState(false);
  const [cannedSearch, setCannedSearch] = useState('');
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ title: '', category: '', body: '' });
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const msgs = await apiFetch(`/support/tickets/${ticket.ticket_id}/messages`, token);
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch {}
  }, [ticket.ticket_id, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (!isAdmin) return;
    apiFetch('/support/canned-responses', token).then(r => setCannedResponses(Array.isArray(r) ? r : [])).catch(() => {});
  }, [isAdmin, token]);

  const loadCanned = () =>
    apiFetch('/support/canned-responses', token).then(r => setCannedResponses(Array.isArray(r) ? r : [])).catch(() => {});

  const handleCreateTemplate = async () => {
    try {
      await apiFetch('/support/canned-responses', token, {
        method: 'POST',
        body: JSON.stringify({ title: newTemplate.title, body: newTemplate.body, category: newTemplate.category || null }),
      });
      setNewTemplate({ title: '', category: '', body: '' });
      setShowNewTemplate(false);
      await loadCanned();
    } catch (e) { alert('Failed to save template: ' + e.message); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Delete this template?')) return;
    try {
      await apiFetch(`/support/canned-responses/${id}`, token, { method: 'DELETE' });
      await loadCanned();
    } catch (e) { alert('Failed to delete template: ' + e.message); }
  };

  const handleSend = async () => {
    if (!text.trim() && !attachFile) return;
    setSending(true);
    try {
      let attachUrl = null;
      if (attachFile) {
        setUploading(true);
        try {
          const upload = await uploadAttachmentToS3(token, ticket.ticket_id, attachFile);
          attachUrl = upload.url;
        } catch (uploadErr) {
          alert('Attachment upload failed: ' + uploadErr.message);
          return;
        } finally { setUploading(false); }
      }
      await apiFetch(`/support/tickets/${ticket.ticket_id}/messages`, token, {
        method: 'POST',
        body: JSON.stringify({ message_text: text || `📎 ${attachFile?.name}`, is_internal: isInternal, attachment_path: attachUrl }),
      });
      setText(''); setAttachFile(null); setIsInternal(false);
      await load();
    } catch (err) { alert(err.message); }
    finally { setSending(false); }
  };

  const isClosed = ticket.status === 'Closed';
  const isPlatformAdmin = isPlatformScoped(user) && !user?.partnerId;
  const filteredCanned = cannedResponses.filter(r =>
    r.title.toLowerCase().includes(cannedSearch.toLowerCase()) ||
    (r.category || '').toLowerCase().includes(cannedSearch.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50 dark:bg-gray-900/30">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-16 text-gray-400 dark:text-gray-500">
            <MessageSquare className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm">No messages yet. Start the conversation.</p>
          </div>
        )}
        {messages.map(msg => {
          const isMine = msg.sender_id === currentUserId;
          const isNote = msg.is_internal;
          return (
            <div key={msg.message_id} className={`flex gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${isNote ? 'bg-gray-400 dark:bg-gray-600' : isMine ? 'bg-primary' : 'bg-sky-500'}`}>
                {isNote ? '🔒' : msg.sender_role?.charAt(0)}
              </div>
              <div className={`max-w-[70%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                <div className={`text-xs text-gray-400 dark:text-gray-500 mb-1 ${isMine ? 'text-right' : ''}`}>
                  {msg.sender_role} · {formatRelativeTime(msg.created_at)}
                  {isNote && <span className="ml-1 text-gray-400 dark:text-gray-500 italic">· Internal Note</span>}
                </div>
                <div className={`px-3 py-2 rounded-xl text-sm leading-relaxed ${
                  isNote
                    ? 'bg-gray-100 dark:bg-gray-700/60 border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                    : isMine
                      ? 'bg-primary text-white'
                      : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 shadow-sm'
                }`}>
                  {msg.message_text}
                  {msg.attachment_path && (
                    <div className="mt-1.5">
                      <a href={msg.attachment_path} target="_blank" rel="noopener noreferrer" className={`text-xs underline ${isMine ? 'text-primary' : 'text-primary dark:text-primary'} flex items-center gap-1`}>
                        <Paperclip className="w-3 h-3" /> Attachment
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply Box */}
      {!isClosed && (
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 relative">
          {/* Canned response picker */}
          {showCanned && (
            <div className="absolute bottom-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl z-10 flex flex-col mx-3 mb-1 overflow-hidden" style={{maxHeight:'320px'}}>
              {/* Header: search + new */}
              <div className="p-2 border-b border-gray-100 flex gap-2 items-center">
                <input autoFocus value={cannedSearch} onChange={e => setCannedSearch(e.target.value)}
                  placeholder="Search templates..." className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                {isPlatformAdmin && (
                  <button onClick={() => setShowNewTemplate(v => !v)}
                    className={`flex items-center gap-1 px-2 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${showNewTemplate ? 'bg-primary text-white border-primary' : 'text-primary border-primary/20 hover:bg-primary/10'}`}>
                    <Plus className="w-3 h-3" /> New
                  </button>
                )}
              </div>

              {/* Inline create form */}
              {showNewTemplate && (
                <div className="p-3 border-b border-indigo-50 bg-primary/10/50 space-y-2">
                  <input value={newTemplate.title} onChange={e => setNewTemplate(p => ({...p, title: e.target.value}))}
                    placeholder="Template title *" className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  <select value={newTemplate.category} onChange={e => setNewTemplate(p => ({...p, category: e.target.value}))}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/30">
                    <option value="" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">Category (optional)</option>
                    {['General', 'Technical', 'Billing', 'Access', 'Other'].map(c => <option key={c} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{c}</option>)}
                  </select>
                  <textarea value={newTemplate.body} onChange={e => setNewTemplate(p => ({...p, body: e.target.value}))}
                    placeholder="Template body *" rows={3}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setShowNewTemplate(false); setNewTemplate({title:'',category:'',body:''}); }}
                      className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                    <button onClick={handleCreateTemplate} disabled={!newTemplate.title.trim() || !newTemplate.body.trim()}
                      className="px-3 py-1 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors">
                      Save Template
                    </button>
                  </div>
                </div>
              )}

              {/* Template list */}
              <div className="overflow-y-auto flex-1">
                {filteredCanned.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-xs text-gray-400 mb-1">No templates found</p>
                    <p className="text-[11px] text-gray-300">Click "+ New" above to create one</p>
                  </div>
                )}
                {filteredCanned.map(r => (
                  <div key={r.id} className="flex items-start gap-1 px-3 py-2.5 hover:bg-primary/10 border-b border-gray-50 last:border-0 group">
                    <button onClick={() => { setText(t => t + r.body); setShowCanned(false); }}
                      className="flex-1 text-left">
                      <div className="font-medium text-gray-800 text-sm">{r.title}</div>
                      {r.category && <div className="text-xs text-primary mt-0.5">{r.category}</div>}
                      <div className="text-xs text-gray-400 mt-0.5 line-clamp-1">{r.body}</div>
                    </button>
                    {isPlatformAdmin && (
                      <button onClick={() => handleDeleteTemplate(r.id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-all p-1 shrink-0">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="p-2 border-t border-gray-100 text-right">
                <button onClick={() => { setShowCanned(false); setShowNewTemplate(false); }} className="text-xs text-gray-400 hover:text-gray-600">Close</button>
              </div>
            </div>
          )}


          <textarea value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }}
            placeholder={isAdmin ? (isInternal ? '🔒 Internal note (tenant cannot see)...' : 'Reply to tenant...') : 'Type your message...'}
            rows={2}
            className={`w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500 ${isInternal ? 'border-dashed border-gray-300 dark:border-gray-500 bg-gray-50 dark:bg-gray-700/60' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'}`}
          />

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <label className="flex-1 min-w-0 flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 truncate">
              <Paperclip className="w-3 h-3 shrink-0" />
              <span className="truncate">{attachFile ? attachFile.name : 'Attach file…'}</span>
              <input type="file" className="hidden" onChange={e => setAttachFile(e.target.files?.[0] || null)} />
            </label>
            {attachFile && (
              <button onClick={() => setAttachFile(null)} className="text-xs text-gray-400 hover:text-red-500"><X className="w-3 h-3" /></button>
            )}

            {isAdmin && (
              <>
                <button onClick={() => { setShowCanned(v => !v); setCannedSearch(''); }}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary dark:text-primary border border-primary/20 dark:border-primary/40 rounded-lg hover:bg-primary/10 dark:hover:bg-primary/80/30 transition-colors">
                  <MessageSquare className="w-3 h-3" /> Template
                </button>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-500 dark:text-gray-400">
                  <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="rounded accent-primary" />
                  <Lock className="w-3 h-3" /> Internal
                </label>
              </>
            )}

            <button onClick={handleSend} disabled={sending || !text.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-lg transition-colors">
              {sending ? <DynamicIcon name="Loader2" className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Reassign / Take Ticket Panel ───────────────────────────────────────────
function AssignPanel({ ticketId, ticket, token, user, onAssigned }) {
  const [agents, setAgents]     = useState([]);
  const [targetId, setTargetId] = useState('');
  const [note, setNote]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [loadingA, setLoadingA] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const partnerId   = ticket?.partner_id;
  // Show "Take Ticket" if the ticket is NOT already assigned to the current user
  const canTake = ticket?.assigned_to !== user?.id;

  const fetchAgents = async () => {
    if (!partnerId) return;
    setLoadingA(true);
    try {
      const list = await apiFetch(`/support/agents?partner_id=${partnerId}`, token);
      setAgents(Array.isArray(list) ? list : []);
      setTargetId('');
    } catch { setAgents([]); }
    finally { setLoadingA(false); }
  };

  const handleExpand = () => {
    if (!expanded) fetchAgents();
    setExpanded(e => !e);
  };

  // "Take Ticket" — self-assign in one click
  const doTake = async () => {
    setLoading(true);
    try {
      await apiFetch(`/support/tickets/${ticketId}/assign`, token, {
        method: 'POST',
        body: JSON.stringify({ assign_to_user_id: user.id, note: 'Taken by agent.' }),
      });
      onAssigned();
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  // Reassign to selected agent
  const doAssign = async () => {
    if (!targetId) return alert('Please select an agent.');
    setLoading(true);
    try {
      await apiFetch(`/support/tickets/${ticketId}/assign`, token, {
        method: 'POST',
        body: JSON.stringify({ assign_to_user_id: targetId, note: note || undefined }),
      });
      setExpanded(false); setNote(''); setTargetId('');
      onAssigned();
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 text-xs overflow-hidden">
      {/* Take Ticket — shown when ticket is not already yours */}
      {canTake && (
        <button onClick={doTake} disabled={loading}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {loading ? '…' : ticket?.assigned_to ? '✋ Take Over Ticket' : '✋ Take Ticket'}
        </button>
      )}

      {/* Reassign to another agent */}
      <button onClick={handleExpand}
        className="w-full flex items-center justify-between px-3 py-2 font-semibold text-primary hover:bg-primary/10 transition-colors">
        <span>🔄 Reassign to Agent</span>
        <span className="text-primary">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-primary/20">
          <div className="pt-1">
            <label className="text-[10px] text-gray-400 uppercase font-bold">Select Agent</label>
            {loadingA ? (
              <p className="text-[10px] text-gray-400 mt-1">Loading agents…</p>
            ) : agents.length === 0 ? (
              <p className="text-[10px] text-red-400 mt-1">No agents found for this partner.</p>
            ) : (
              <select value={targetId} onChange={e => setTargetId(e.target.value)}
                className="w-full mt-1 px-2 py-1.5 text-xs border border-primary/20 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value="" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">-- Select agent --</option>
                {agents.map(a => (
                  <option key={a.id} value={a.id} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{a.name} ({a.email})</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="text-[10px] text-gray-400 uppercase font-bold">Note (optional)</label>
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder="Reason for reassignment…"
              className="w-full mt-1 px-2 py-1.5 text-xs border border-primary/20 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30 bg-white" />
          </div>

          <button onClick={doAssign} disabled={loading || !targetId}
            className="w-full py-1.5 text-xs font-bold rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors">
            {loading ? 'Assigning…' : 'Confirm Reassign'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Ticket Detail (3-pane right side) ────────────────────────────────────────
function TicketDetail({ ticketId, isAdmin, token, currentUserId, user, onBack, onRefresh }) {
  const { formatDate: fmtDate, formatRelativeTime: fmtRelative } = useTimezoneFormatter();
  const [ticket, setTicket] = useState(null);
  const [tab, setTab] = useState('chat');
  const [tagInput, setTagInput] = useState('');
  const [statusChanging, setChanging] = useState(false);

  const load = useCallback(async () => {
    try {
      const t = await apiFetch(`/support/tickets/${ticketId}`, token);
      setTicket(t);
    } catch (err) { console.error(err); }
  }, [ticketId, token]);

  useEffect(() => { load(); }, [load]);

  const doStatus = async (s) => {
    setChanging(true);
    try {
      await apiFetch(`/support/tickets/${ticketId}/status`, token, { method: 'PATCH', body: JSON.stringify({ status: s }) });
      await load(); onRefresh();
    } catch (e) { alert(e.message); }
    finally { setChanging(false); }
  };
  const doEscalate = async () => {
    if (!confirm('Escalate this ticket to Platform Admin?')) return;
    try { await apiFetch(`/support/tickets/${ticketId}/escalate`, token, { method: 'POST' }); await load(); onRefresh(); } catch (e) { alert(e.message); }
  };
  const doClose = async () => {
    if (!confirm('Close this ticket?')) return;
    try { await apiFetch(`/support/tickets/${ticketId}/close`, token, { method: 'POST' }); await load(); onRefresh(); } catch (e) { alert(e.message); }
  };
  const doReopen = async () => {
    try { await apiFetch(`/support/tickets/${ticketId}/reopen`, token, { method: 'POST' }); await load(); onRefresh(); } catch (e) { alert(e.message); }
  };
  const addTag = async () => {
    if (!tagInput.trim()) return;
    try { await apiFetch(`/support/tickets/${ticketId}/tags`, token, { method: 'PATCH', body: JSON.stringify({ add_tags: [tagInput.trim()], remove_tags: [] }) }); setTagInput(''); await load(); } catch (e) { alert(e.message); }
  };
  const removeTag = async (tag) => {
    try { await apiFetch(`/support/tickets/${ticketId}/tags`, token, { method: 'PATCH', body: JSON.stringify({ add_tags: [], remove_tags: [tag] }) }); await load(); } catch (e) { alert(e.message); }
  };

  if (!ticket) return <div className="h-full flex items-center justify-center"><LoadingSpinner /></div>;

  const isPlatformAdmin = isPlatformScoped(user) && !user?.partnerId;
  const isPartnerAdmin  = isPlatformScoped(user) && !!user?.partnerId;
  const isSupportAgent  = isPlatformScoped(user); // Any platform user is an agent in support context

  const nextStatuses = isAdmin ? (VALID_NEXT[ticket.status] || []).filter(s => s !== 'Escalated') : [];
  const isTenant = !isAdmin;
  const canClose     = isTenant && ticket.status === 'Resolved';
  const canReopen    = isTenant && ['Resolved', 'Closed'].includes(ticket.status);
  // Platform Admin cannot escalate (they ARE the top level)
  const canEscalate  = !isPlatformAdmin && !isSupportAgent && ticket.status !== 'Closed' && ticket.status !== 'Escalated';
  // Any support agent or admin can assign (supports "Take Ticket" self-assign)
  const canAssign    = (isSupportAgent || isPartnerAdmin || isPlatformAdmin) &&
                       !['Closed','Resolved'].includes(ticket.status);

  const LEVEL_COLORS = {
    L1: 'bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300',
    L2: 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary',
    L3: 'bg-purple-50 text-purple-600 dark:bg-purple-900/40 dark:text-purple-300',
    Partner: 'bg-orange-50 text-orange-600 dark:bg-orange-900/40 dark:text-orange-300',
    Platform: 'bg-red-50 text-red-600 dark:bg-red-900/40 dark:text-red-300' 
  };

  return (
    <div className="flex h-full w-full">
      {/* Left info pane */}
      <div className="w-72 shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col bg-white dark:bg-gray-800 overflow-y-auto">
        {/* Action bar */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-1 text-xs font-medium text-primary dark:text-primary hover:text-primary dark:hover:text-primary transition-colors">
            <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Back
          </button>
          <span className="text-xs font-bold text-primary dark:text-primary">{ticket.display_number}</span>
        </div>

        <div className="p-4 space-y-4">
          {/* Subject */}
          <div>
            <h2 className="font-bold text-gray-900 dark:text-white text-sm leading-snug">{ticket.subject}</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{ticket.email}</p>
          </div>

          {/* Status & Priority */}
          <div className="flex flex-wrap gap-2">
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
            {ticket.category && <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">{ticket.category}</span>}
          </div>

          {/* Support Tier Entitlement Badge — admin only */}
          {isAdmin && ticket.support_tier && (() => {
            const tierStyle = {
              Platinum: { bg: 'bg-violet-50 dark:bg-violet-900/30', border: 'border-violet-200 dark:border-violet-700', text: 'text-violet-700 dark:text-violet-300', icon: '💎' },
              Gold:     { bg: 'bg-amber-50  dark:bg-amber-900/30',  border: 'border-amber-200  dark:border-amber-700',  text: 'text-amber-700  dark:text-amber-300',  icon: '⭐' },
              Silver:   { bg: 'bg-slate-50  dark:bg-slate-700/40',  border: 'border-slate-200  dark:border-slate-600',  text: 'text-slate-600  dark:text-slate-300',  icon: '🪨' },
              Basic:    { bg: 'bg-gray-50   dark:bg-gray-700/40',   border: 'border-gray-200   dark:border-gray-600',   text: 'text-gray-500   dark:text-gray-400',   icon: '🚫' },
            };
            const s = tierStyle[ticket.support_tier] || tierStyle.Basic;
            const slaMap = { Platinum: '1h response · 12h resolve', Gold: '4h response · 2d resolve', Silver: '24h response · 7d resolve', Basic: '48h response · 14d resolve' };
            return (
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${s.bg} ${s.border}`}>
                <span className={`text-xs font-bold ${s.text}`}>{s.icon} {ticket.support_tier} Plan</span>
                <span className={`text-[10px] ${s.text} opacity-80`}>{slaMap[ticket.support_tier]}</span>
              </div>
            );
          })()}

          {/* Ownership Panel — all admins */}
          {isAdmin && (ticket.l1_owner_id || ticket.assigned_to) && (
            <div className="rounded-xl border border-blue-100 overflow-hidden text-xs">
              <div className="px-3 py-2 bg-gradient-to-r from-blue-600 to-primary">
                <span className="text-[10px] font-bold text-white/90 uppercase tracking-widest">Agent</span>
              </div>
              <div className="bg-blue-50/40 divide-y divide-blue-50">
                {ticket.l1_owner_name && (
                  <div className="flex justify-between items-center px-3 py-2">
                    <span className="flex items-center gap-1 text-gray-400"><span>👤</span> Assigned</span>
                    <span className="font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px]">{ticket.l1_owner_name}</span>
                  </div>
                )}
                {ticket.escalation_count > 0 && (
                  <div className="flex justify-between items-center px-3 py-2">
                    <span className="flex items-center gap-1 text-gray-400"><span>🔁</span> Escalations</span>
                    <span className="font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full text-[10px]">{ticket.escalation_count}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Assign / Take Ticket — Support Agent or Admin */}
          {canAssign && <AssignPanel ticketId={ticketId} ticket={ticket} token={token} user={user} onAssigned={load} />}

          {/* SLA */}
          <div className="py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-700 text-xs space-y-1.5">
            <div className="flex justify-between">
              <span className="text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-wide text-[10px]">SLA Status</span>
              <SLABadge ticket={ticket} />
            </div>
            {ticket.sla_response_due_at && (
              <div className="flex justify-between">
                <span className="text-gray-400 dark:text-gray-500">Response due</span>
                <span className="text-gray-600 dark:text-gray-300">{fmtDate(ticket.sla_response_due_at)}</span>
              </div>
            )}
            {ticket.sla_resolve_due_at && (
              <div className="flex justify-between">
                <span className="text-gray-400 dark:text-gray-500">Resolve due</span>
                <span className="text-gray-600 dark:text-gray-300">{fmtDate(ticket.sla_resolve_due_at)}</span>
              </div>
            )}
          </div>

          {/* Build Version */}
          {ticket.build_version && (
            <div className="py-2 px-3 bg-indigo-50/50 dark:bg-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800/50 text-xs flex justify-between items-center">
              <span className="text-indigo-600 dark:text-indigo-400 font-semibold flex items-center gap-1">
                <Shield className="w-3 h-3" /> Build Version
              </span>
              <span className="font-mono text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">
                v{ticket.build_version}
              </span>
            </div>
          )}

          {/* Description */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Description</p>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-700/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">{ticket.description}</p>
          </div>

          {/* Tags */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Tags</p>
            <div className="flex flex-wrap gap-1.5">
              {(ticket.tags || []).map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 text-xs bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary border border-primary/20 dark:border-primary/40 px-2 py-0.5 rounded-full">
                  #{tag}
                  {isAdmin && <button onClick={() => removeTag(tag)} className="text-primary hover:text-primary dark:hover:text-primary ml-0.5">×</button>}
                </span>
              ))}
              {isAdmin && (
                <div className="flex gap-1 w-full mt-1">
                  <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addTag()}
                    placeholder="Add tag…"
                    className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  <button onClick={addTag} className="px-2 py-1 text-xs bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary border border-primary/20 dark:border-primary/40 rounded-lg hover:bg-primary/15 dark:hover:bg-primary/80/50 transition-colors">+</button>
                </div>
              )}
            </div>
          </div>

          {/* Admin status change */}
          {isAdmin && nextStatuses.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Change Status</p>
              <div className="space-y-1.5">
                {nextStatuses.map(s => {
                  const m = STATUS_META[s] || {};
                  return (
                    <button key={s} onClick={() => doStatus(s)} disabled={statusChanging}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors hover:bg-gray-50 text-gray-700 border-gray-200`}>
                      <span className={`w-2 h-2 rounded-full ${m.dot}`} /> → {m.label || s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-2">
            {canEscalate && (
              <button onClick={doEscalate} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition-colors">
                <ArrowUpCircle className="w-3.5 h-3.5" /> Escalate to Platform Admin
              </button>
            )}
            {canClose && (
              <button onClick={doClose} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-green-600 border border-green-200 rounded-xl hover:bg-green-50 transition-colors">
                <CheckCircle2 className="w-3.5 h-3.5" /> Close Ticket
              </button>
            )}
            {canReopen && (
              <button onClick={doReopen} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-yellow-600 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-900/50 rounded-xl hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors">
                <RefreshCw className="w-3.5 h-3.5" /> Reopen Ticket
              </button>
            )}
            {ticket.attachment_path && ticket.attachment_path.startsWith('http') && (
              <button
                onClick={async () => {
                  try {
                    // Call backend proxy — raw S3 URL stays server-side, no CORS issue
                    const { blob, filename } = await apiFetchBlob(
                      `/support/tickets/${ticket.ticket_id}/attachment`,
                      token
                    );
                    const blobUrl = URL.createObjectURL(blob);
                    const win = window.open(blobUrl, '_blank');
                    if (win) {
                      win.addEventListener('load', () => URL.revokeObjectURL(blobUrl), { once: true });
                    } else {
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
                    }
                  } catch (err) {
                    alert(`Unable to open attachment: ${err.message}`);
                  }
                }}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-primary border border-primary/20 rounded-xl hover:bg-primary/10 transition-colors">
                <Paperclip className="w-3.5 h-3.5" /> View Attachment
              </button>
            )}
          </div>
        </div>

        {/* Timeline / History */}
        {(ticket.history || []).length > 0 && (
          <div className="px-4 pb-4">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">Timeline</p>
            <div className="space-y-2">
              {ticket.history.map(h => (
                <div key={h.history_id} className="flex gap-2 items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div>
                    <div className="text-xs text-gray-600">{h.action.replace(/_/g, ' ')}</div>
                    {h.new_value && <div className="text-[11px] text-gray-400">→ {h.new_value}</div>}
                    <div className="text-[11px] text-gray-300 dark:text-gray-600">{fmtRelative(h.changed_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: Chat */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tabs */}
        <div className="px-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center gap-6 shrink-0">
          {['chat', 'overview'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`py-3 text-sm font-medium border-b-2 transition-colors capitalize ${tab === t ? 'border-primary text-gray-900 dark:text-white' : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}>
              {t === 'chat' ? 'Chat Thread' : 'Overview'}
            </button>
          ))}
          {isAdmin && (
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Internal notes hidden from tenant
            </span>
          )}
        </div>

        {tab === 'chat' ? (
          <div className="flex-1 overflow-hidden">
            <ChatPanel ticket={ticket} isAdmin={isAdmin} token={token} currentUserId={currentUserId} user={user} />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30 dark:bg-gray-900/20">
            <div className="max-w-2xl space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Ticket Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    ['Ticket ID', ticket.display_number],
                    ['Status', ticket.status],
                    ['Priority', ticket.priority],
                    ['Category', ticket.category || '—'],
                    ['Assigned Role', ticket.assigned_role || '—'],
                    ['Created', fmtDate(ticket.created_at)],
                    ['First Response', ticket.first_response_at ? fmtDate(ticket.first_response_at) : 'Not yet'],
                    ['Resolved At', ticket.resolved_at ? fmtDate(ticket.resolved_at) : '—'],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
                      <p className="text-sm text-gray-800">{val}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-2">Description</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{ticket.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main SupportPage ─────────────────────────────────────────────────────────
export default function SupportPage({ user }) {
  const { formatRelativeTime } = useTimezoneFormatter();
  const { token } = useAuth();
  // All admin-side roles: platform admins, partner admin, support admin, and support agents
  const isAdmin = isPlatformScoped(user);

  const [tickets, setTickets]         = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [selectedId, setSelectedId]   = useState(null);
  const [showCreate, setShowCreate]   = useState(false);

  // Filters
  const [activeTab, setActiveTab]     = useState('All');
  const [search, setSearch]           = useState('');
  const [filterPriority, setPriority] = useState('');
  const [filterTenant, setFilterTenant] = useState('');

  // Bulk
  const [selected, setSelected]       = useState(new Set());
  const [bulkAction, setBulkAction]   = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'All') params.set('status', activeTab);
      if (search)              params.set('search', search);
      if (filterPriority)      params.set('priority', filterPriority);

      const data = await apiFetch(`/support/tickets?${params}`, token);
      setTickets(data?.items || []);

      if (isAdmin) {
        apiFetch('/support/admin/dashboard', token).then(setStats).catch(() => {});
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [activeTab, search, filterPriority, isAdmin, token]);

  useEffect(() => { load(); }, [load]);

  const tabs = isAdmin
    ? ['All', 'Open', 'InProgress', 'WaitingForCustomer', 'Escalated', 'Resolved', 'Closed']
    : ['All', 'Open', 'InProgress', 'WaitingForCustomer', 'Resolved', 'Closed'];

  // Unique tenant names from fetched tickets — used for tenant filter dropdown
  const uniqueTenants = isAdmin
    ? [...new Set(tickets.map(t => t.tenant_name).filter(Boolean))].sort()
    : [];

  const handleBulkAction = async () => {
    if (!selected.size || !bulkAction) return;
    const ids = Array.from(selected);
    try {
      if (bulkAction === 'close') await apiFetch('/support/tickets/bulk/close', token, { method: 'PATCH', body: JSON.stringify({ ticket_ids: ids }) });
      if (bulkAction === 'escalate') await apiFetch('/support/tickets/bulk/status', token, { method: 'PATCH', body: JSON.stringify({ ticket_ids: ids, status: 'Escalated' }) });
      setSelected(new Set()); setBulkAction('');
      await load();
    } catch (e) { alert(e.message); }
  };

  if (selectedId) {
    return (
      <div className="flex h-full w-full bg-gray-50 dark:bg-gray-900 overflow-hidden">
        <TicketDetail ticketId={selectedId} isAdmin={isAdmin} token={token} currentUserId={user?.id} user={user} onBack={() => setSelectedId(null)} onRefresh={load} />
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900 overflow-hidden font-sans">
      {/* LEFT NAV PANE */}
      <div className="w-56 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col shrink-0 overflow-y-auto">
        <div className="p-5 pb-2">
          <h1 className="text-base font-bold text-gray-900 dark:text-white">Support Tickets</h1>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{isAdmin ? 'Manage customer tickets' : 'Track your requests'}</p>
        </div>

        {/* Stats for admin */}
        {isAdmin && stats && (
          <div className="px-4 py-3 space-y-1.5">
            {[
              { label: 'Open',       val: stats.open,      color: 'text-blue-600 dark:text-blue-400' },
              { label: 'Escalated',  val: stats.escalated, color: 'text-red-600 dark:text-red-400' },
              { label: 'SLA Breach', val: (stats.sla_response_breached || 0) + (stats.sla_resolve_breached || 0), color: 'text-red-500 dark:text-red-400' },
            ].map(s => (
              <div key={s.label} className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">{s.label}</span>
                <span className={`font-bold ${s.color}`}>{s.val ?? 0}</span>
              </div>
            ))}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-1.5 mt-1.5 flex justify-between text-xs">
              <span className="text-gray-400 dark:text-gray-500">Total</span>
              <span className="font-semibold text-gray-700 dark:text-gray-300">{stats.total ?? 0}</span>
            </div>
          </div>
        )}

        <div className="px-3 py-3 space-y-0.5 flex-1">
          {tabs.map(tab => {
            const m = STATUS_META[tab];
            const count = tab === 'All' ? tickets.length : tickets.filter(t => t.status === tab).length;
            return (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab
                    ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}>
                <div className="flex items-center gap-2">
                  {m && <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />}
                  {m?.label || tab}
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab
                    ? 'bg-primary/15 dark:bg-primary/20 text-primary dark:text-primary'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>{count}</span>
              </button>
            );
          })}
        </div>

        {!isAdmin && (
          <div className="p-3">
            <button onClick={() => setShowCreate(true)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-xl transition-colors shadow-sm">
              <Plus className="w-4 h-4" /> New Ticket
            </button>
          </div>
        )}
      </div>

      {/* TICKET LIST PANE */}
      <div className="w-96 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col shrink-0">
        {/* Search + Filters */}
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search tickets..."
              className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="flex gap-2">
            <select value={filterPriority} onChange={e => setPriority(e.target.value)}
              className="flex-1 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none">
              <option value="" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">All Priority</option>
              {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{p}</option>)}
            </select>
            <button onClick={load} className="px-2.5 py-1.5 text-xs text-primary dark:text-primary border border-primary/20 dark:border-primary/40 rounded-lg hover:bg-primary/10 dark:hover:bg-primary/80/30 transition-colors">
              <RefreshCw className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" />
            </button>
            {isAdmin && (
              <button onClick={() => setShowCreate(true)}
                className="px-2.5 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {/* Tenant Filter — visible only for Platform Admin (r1/r2) and Partner Admin (r3) */}
          {isAdmin && uniqueTenants.length > 0 && ['r1', 'r2', 'r3'].includes(user?.roleId) && (
            <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">🏢 All Tenants ({uniqueTenants.length})</option>
              {uniqueTenants.map(t => <option key={t} value={t} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{t}</option>)}
            </select>
          )}
        </div>

        {/* Bulk action bar */}
        {isAdmin && selected.size > 0 && (
          <div className="px-3 py-2 border-b border-primary/20 bg-primary/10 flex items-center gap-2">
            <span className="text-xs font-semibold text-primary">{selected.size} selected</span>
            <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}
              className="flex-1 text-xs px-2 py-1 border border-primary/20 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none">
              <option value="" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">Action…</option>
              <option value="close" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">Close</option>
              <option value="escalate" className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">Escalate</option>
            </select>
            <button onClick={handleBulkAction} disabled={!bulkAction}
              className="text-xs px-2 py-1 bg-primary text-white rounded-lg disabled:opacity-50">Apply</button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-primary hover:text-primary">✕</button>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
          {(() => {
            const displayed = filterTenant
              ? tickets.filter(t => t.tenant_name === filterTenant)
              : tickets;
            if (loading) return <div className="p-8 flex justify-center"><LoadingSpinner /></div>;
            if (displayed.length === 0) return (
              <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">{filterTenant ? `No tickets from “${filterTenant}”` : 'No tickets found'}</p>
                {!isAdmin && !filterTenant && (
                  <button onClick={() => setShowCreate(true)} className="mt-3 text-xs text-primary dark:text-primary hover:underline">+ Create your first ticket</button>
                )}
                {filterTenant && (
                  <button onClick={() => setFilterTenant('')} className="mt-2 text-xs text-primary hover:underline">Clear filter</button>
                )}
              </div>
            );
            return displayed.map(ticket => {
              const isSelected = selected.has(ticket.ticket_id);
              const m = STATUS_META[ticket.status] || STATUS_META.Open;
              return (
                <div key={ticket.ticket_id}
                  onClick={() => setSelectedId(ticket.ticket_id)}
                  className={`p-3 cursor-pointer transition-colors border-l-4 relative ${
                    selectedId === ticket.ticket_id
                      ? 'bg-blue-50/50 dark:bg-primary/20 border-primary'
                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}>

                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isAdmin && (
                        <div onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={isSelected} onChange={e => {
                            const next = new Set(selected);
                            e.target.checked ? next.add(ticket.ticket_id) : next.delete(ticket.ticket_id);
                            setSelected(next);
                          }} className="rounded accent-primary mr-1" />
                        </div>
                      )}
                      <span className="text-xs font-bold text-primary dark:text-primary shrink-0">{ticket.display_number}</span>
                    </div>
                    <StatusBadge status={ticket.status} />
                  </div>

                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate pr-2 mb-1">{ticket.subject}</h3>

                  {isAdmin && ticket.tenant_name && (
                    <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                      <span className="text-[10px] font-semibold bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 border border-sky-100 dark:border-sky-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                        🏢 {ticket.tenant_name}
                      </span>
                      {ticket.support_tier && ticket.support_tier !== 'Basic' && (() => {
                        const t = { Platinum: { cls: 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30', icon: '💎' }, Gold: { cls: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30', icon: '⭐' }, Silver: { cls: 'text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/50', icon: '🪨' } };
                        const s = t[ticket.support_tier];
                        return s ? <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.cls}`}>{s.icon} {ticket.support_tier}</span> : null;
                      })()}
                      {ticket.assigned_role && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          ticket.assigned_role === 'PlatformAdmin'
                            ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                            : 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary'
                        }`}>
                          → {ticket.assigned_role === 'PlatformAdmin' ? 'Platform' : 'Partner'}
                        </span>
                      )}
                    </div>
                  )}


                  <div className="flex items-center gap-2 flex-wrap">
                    <PriorityBadge priority={ticket.priority} />
                    {ticket.category && <span className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">{ticket.category}</span>}
                    <SLABadge ticket={ticket} formatRelativeTime={formatRelativeTime} />
                  </div>

                  <div className="flex items-center text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                    <Clock className="w-3 h-3 mr-1" /> {formatRelativeTime(ticket.created_at)}
                    {(ticket.tags || []).length > 0 && (
                      <span className="ml-2 flex items-center gap-1">
                        <Tag className="w-2.5 h-2.5" />
                        {(ticket.tags || []).slice(0, 2).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* DETAIL PANE */}
      <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 overflow-hidden">
        {selectedId ? (
          <TicketDetail ticketId={selectedId} isAdmin={isAdmin} token={token} currentUserId={user?.id} user={user} onBack={() => setSelectedId(null)} onRefresh={load} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 bg-gray-50/50 dark:bg-gray-900/20">
            <div className="w-16 h-16 bg-white dark:bg-gray-700 rounded-full shadow-sm flex items-center justify-center mb-4 border border-gray-100 dark:border-gray-600">
              <LifeBuoy className="w-8 h-8 text-gray-300 dark:text-gray-500" />
            </div>
            <h3 className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1">Select a ticket</h3>
            <p className="text-sm text-gray-400 max-w-xs text-center">Click a ticket from the list to view its details, chat thread, and take action.</p>
            {!isAdmin && (
              <button onClick={() => setShowCreate(true)} className="mt-5 flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-xl transition-colors shadow-sm">
                <Plus className="w-4 h-4" /> New Ticket
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <CreateTicketModal token={token} onClose={() => setShowCreate(false)}
          onCreated={t => { setShowCreate(false); setSelectedId(t.ticket_id); load(); }} />
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl shadow-lg">
          ⚠️ {error}
        </div>
      )}
    </div>
  );
}
